import { ReadonlyVec3 } from 'gl-matrix';
import { domainEventBus, RoiContoursModifiedEvent } from '~/domain/events';
import { DomainEventSubscription } from '~/domain/events/DomainEventBus';
import { Observer } from '~/engine/core/observable';
import { Grid } from '~/engine/data/common/Grid';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { ViewManager } from '~/engine/view/ViewManager';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { extractSlicePolygons } from '~/engine/core/utils/marchingSquares';
import {
    blendSlicesBSpline,
    CONTOUR_AUTO_SUGGESTION_OPERATION,
    SliceWithPosition,
} from './computeSliceSuggestion';
import { SignedDistanceField } from '~/engine/core/models/SignedDistanceField';
import {
    EdgeRefinementOptions,
    estimateRobustIntensitySpread,
    extractIntensitySliceAlignedToGrid,
    refineSliceWithIntensityEdge,
    refineSliceWithIntensityRegion,
    RegionRefinementOptions,
    scaleRefinementOptionsForSpread,
} from '~/engine/util/algorithms/sdf/IntensityRefinement';
import { Modality } from '~/domain/dicom/modality';
import { ContourAutoSuggestionPreviewRenderer } from './ContourAutoSuggestionPreviewRenderer';
import { lightenColor } from '~/utils/colorUtils';

/**
 * Delay before a suggestion is computed and rendered after the user
 * lands on a new slice. This gives the user time to scroll past
 * intermediate slices without seeing the ghost flicker.
 */
export const CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS = 500;

type ActiveSuggestion = {
    roiNumber: number;
    targetVoxelZ: number;
    /**
     * The exact pixel data the renderer used to produce the
     * displayed ghost — either a raw copy of one neighbour slice or
     * a midpoint blend of both. {@link accept} writes these bytes
     * directly so the committed contour matches what the user saw.
     */
    sourceSliceBytes: Uint8Array;
};

/**
 * Singleton controller for the slice-to-slice contour deformation
 * suggestion feature. Lifecycle:
 *
 *  1. {@link enable} is called by a React hook when the toggle is
 *     activated, given the transverse {@link PlaneView} and a
 *     resolver returning the currently active {@link RegionOfInterest}.
 *  2. The controller subscribes to:
 *      - the active structure's {@link RoiContoursModifiedEvent}
 *        stream, so a fresh draw discards any stale ghost and the
 *        re-evaluation can pick up an updated source slice;
 *      - the view's plane-intersection observer, scheduling a
 *        delayed evaluation whenever the user lands on a new slice;
 *      - the view's pan/zoom observer, refreshing the ghost so it
 *        stays anchored to patient coordinates;
 *      - document-level capture-phase keydown / mousedown listeners
 *        for the approval gestures, plus a bubble-phase mousemove
 *        listener that repaints the ghost after tools that clear the
 *        shared 2D overlay (see below).
 *  3. The interaction model offers three actions on a rendered
 *     suggestion:
 *      - **accept** ({@link accept}) on `Ctrl + left-click`: commits
 *        the contour via the standard SDF write pipeline (audit
 *        operation {@link CONTOUR_AUTO_SUGGESTION_OPERATION}) and stays on the
 *        current slice. The capture-phase listener consumes the click
 *        so the active tool does not also start an action.
 *      - **draw-to-tweak** on a plain `left-click` (brush only,
 *        gated by {@link allowPlainClickTweak}): materialises the
 *        suggestion synchronously ({@link accept}) and then lets the
 *        brush stroke through, so the user's stroke edits the
 *        now-real contour instead of a blank slice. Disabled for
 *        tools like line-deform, where plain clicks drive the tool's
 *        own interaction and the ghost persists until Ctrl+click.
 *      - **discard** ({@link discardPending}) on `Esc`, or
 *        automatically on slice change / disable.
 *  4. {@link disable} clears all subscriptions, the pending timer
 *     and any rendered ghost.
 *
 * Suggestions are held entirely in-memory until accepted (or
 * discarded by navigation / Esc).
 */
export class ContourAutoSuggestionController {
    private static instance: ContourAutoSuggestionController | undefined;

    public static getInstance(): ContourAutoSuggestionController {
        if (!ContourAutoSuggestionController.instance) {
            ContourAutoSuggestionController.instance =
                new ContourAutoSuggestionController();
        }
        return ContourAutoSuggestionController.instance;
    }

    private enabled = false;
    private view: PlaneView | undefined;
    private getRoi: (() => RegionOfInterest | undefined) | undefined;
    private renderer: ContourAutoSuggestionPreviewRenderer | undefined;

    private pending: ActiveSuggestion | undefined;
    private pendingTimer: ReturnType<typeof setTimeout> | undefined;

    /**
     * Whether a plain (unmodified) left-click should materialise the
     * pending suggestion so the ensuing stroke edits it
     * ("draw-to-tweak"). Enabled for the brush; disabled for tools
     * like line-deform whose plain clicks drive their own
     * interaction — there the suggestion is only ever committed via
     * Ctrl+click. Set per-activation by {@link enable}.
     */
    private allowPlainClickTweak = false;

    private contourSubscription: DomainEventSubscription | undefined;
    private planeIntersectionObserver: Observer<ReadonlyVec3> | undefined;
    private panZoomObserver: Observer<number> | undefined;
    private keydownHandler: ((e: KeyboardEvent) => void) | undefined;
    private mousedownHandler: ((e: MouseEvent) => void) | undefined;
    private mousemoveHandler: ((e: MouseEvent) => void) | undefined;

    private constructor() {}

    public isEnabled(): boolean {
        return this.enabled;
    }

    public hasPendingSuggestion(): boolean {
        return this.pending !== undefined;
    }

    public enable(
        view: PlaneView,
        getRoi: () => RegionOfInterest | undefined,
        allowPlainClickTweak = false
    ): void {
        if (this.enabled) {
            this.disable();
        }
        this.enabled = true;
        this.view = view;
        this.getRoi = getRoi;
        this.allowPlainClickTweak = allowPlainClickTweak;

        try {
            this.renderer = new ContourAutoSuggestionPreviewRenderer(view);
        } catch (err) {
            console.warn(
                'ContourAutoSuggestionController: cannot create renderer',
                err
            );
            this.renderer = undefined;
        }

        this.contourSubscription = domainEventBus.subscribe(
            RoiContoursModifiedEvent.EVENT_TYPE,
            this.onContoursModified.bind(this)
        );

        this.planeIntersectionObserver = (_value: ReadonlyVec3) => {
            this.onPlaneIntersectionChanged();
        };
        view.planeIntersectionPoint.subscribe(this.planeIntersectionObserver);

        this.panZoomObserver = (_value: number) => {
            this.renderer?.refresh();
        };
        view.panZoom.subscribe(this.panZoomObserver);

        // Approval gestures — keydown / mousedown registered in the
        // capture phase so they preempt the engine's bubble-phase
        // canvas handlers (ViewManager.handleEvent), letting Ctrl+click
        // commit before the active tool starts its own action and
        // preserving existing Esc behaviours when no suggestion is
        // pending.
        this.keydownHandler = this.onDocumentKeyDown.bind(this);
        this.mousedownHandler = this.onDocumentMouseDown.bind(this);
        document.addEventListener('keydown', this.keydownHandler, true);
        document.addEventListener('mousedown', this.mousedownHandler, true);

        // Mouse-move repaint registered in the BUBBLE phase so it runs
        // AFTER ViewManager has driven the active tool's render. Tools
        // such as line-deform render their own cursor on the shared 2D
        // overlay and clear the whole canvas first, which would erase
        // the ghost; repainting afterwards keeps the suggestion visible
        // on top of the tool's overlay.
        this.mousemoveHandler = this.onDocumentMouseMove.bind(this);
        document.addEventListener('mousemove', this.mousemoveHandler, false);

        // Evaluate once on enable so a suggestion shows up if the
        // user is already on an empty slice with a populated
        // neighbour, without having to scroll first.
        this.scheduleEvaluation();
    }

    public disable(): void {
        this.enabled = false;

        this.cancelPendingTimer();

        if (this.contourSubscription) {
            this.contourSubscription.unsubscribe();
            this.contourSubscription = undefined;
        }

        if (this.view) {
            if (this.planeIntersectionObserver) {
                this.view.planeIntersectionPoint.unsubscribe(
                    this.planeIntersectionObserver
                );
            }
            if (this.panZoomObserver) {
                this.view.panZoom.unsubscribe(this.panZoomObserver);
            }
        }
        this.planeIntersectionObserver = undefined;
        this.panZoomObserver = undefined;

        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler, true);
            this.keydownHandler = undefined;
        }
        if (this.mousedownHandler) {
            document.removeEventListener(
                'mousedown',
                this.mousedownHandler,
                true
            );
            this.mousedownHandler = undefined;
        }
        if (this.mousemoveHandler) {
            document.removeEventListener(
                'mousemove',
                this.mousemoveHandler,
                false
            );
            this.mousemoveHandler = undefined;
        }

        this.renderer?.dispose();
        this.renderer = undefined;

        this.discardPending();

        this.view = undefined;
        this.getRoi = undefined;
    }

    /**
     * Commit the pending suggestion to the structure SDF, emit the
     * audit event, and trigger a viewport redraw. Returns `true`
     * when the contour was written, `false` otherwise (no pending
     * suggestion, ROI changed, texture missing, approved, or the
     * pending bytes no longer fit the texture grid). The pending
     * suggestion is always cleared either way.
     *
     * Does NOT navigate — the view stays on the current slice after
     * a commit.
     *
     * @param skipUndo When `true`, writes the SDF data directly
     *   without going through the undo/redo pipeline. Used by the
     *   draw-to-tweak gesture so the suggestion materialisation
     *   and the subsequent brush stroke are recorded as a single
     *   undoable operation (the brush's own `startModify` captures
     *   the state including the suggestion as its baseline).
     */
    public accept(skipUndo = false): boolean {
        if (!this.pending || !this.getRoi) {
            return false;
        }
        const roi = this.getRoi();

        if (
            !roi ||
            roi.roiNumber !== this.pending.roiNumber ||
            !roi.texture ||
            !roi.canModify
        ) {
            this.discardPending();
            return false;
        }

        const texture = roi.texture;

        const accepted = this.pending;
        this.discardPending();
        const sizeZ = texture.sizePx[2];
        if (
            accepted.targetVoxelZ < 0 ||
            accepted.targetVoxelZ >= sizeZ ||
            accepted.sourceSliceBytes.length !==
                texture.textureGrid.sizeX * texture.textureGrid.sizeY
        ) {
            console.warn(
                'ContourAutoSuggestionController: pending suggestion no longer aligns with texture grid'
            );
            return false;
        }

        const sliceBytes = new Uint8Array(accepted.sourceSliceBytes);
        const sdf = texture.signedDistanceField;

        if (skipUndo) {
            // Write only the changed slice directly without undo
            // bookkeeping. The follow-up brush stroke will call
            // startModify itself, capturing the post-suggestion
            // state as its undo baseline — so only one undo entry
            // is created.
            try {
                sdf.arrayToTextureSlice(sliceBytes, accepted.targetVoxelZ);
            } catch (err) {
                console.error(
                    'ContourAutoSuggestionController: failed to commit suggestion (silent)',
                    err
                );
                return false;
            }
        } else {
            const modify = roi.startModify(CONTOUR_AUTO_SUGGESTION_OPERATION);
            try {
                sdf.arrayToTextureSlice(sliceBytes, accepted.targetVoxelZ);
                modify.finish();
            } catch (err) {
                modify.cancel();
                console.error(
                    'ContourAutoSuggestionController: failed to commit suggestion',
                    err
                );
                return false;
            }
        }

        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                roi.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
        return true;
    }

    /**
     * Handle a change to the view's plane-intersection point.
     *
     * Plane-intersection updates fire not only on slice scrolls but
     * also on intra-slice movements (crosshair drag, hover-driven
     * snapping, sub-voxel adjustments). When the visible slice has
     * NOT changed we keep the current ghost and just repaint it
     * (cheap), so the suggestion does not flicker away on every
     * mouse move. Only an actual slice change tears the ghost down
     * and schedules a fresh evaluation.
     */
    private onPlaneIntersectionChanged(): void {
        if (!this.enabled) {
            return;
        }
        if (
            this.pending &&
            this.currentSliceZForActiveRoi() === this.pending.targetVoxelZ
        ) {
            this.renderer?.refresh();
            return;
        }
        this.scheduleEvaluation();
    }

    /**
     * Document-level mousemove handler (bubble phase). Repaints the
     * pending ghost so it survives tools that clear the shared 2D
     * overlay on every move (e.g. line-deform draws its cursor after
     * a full `clearRect`). No-op when nothing is pending.
     */
    private onDocumentMouseMove(_e: MouseEvent): void {
        if (this.pending) {
            this.renderer?.refresh();
        }
    }

    /**
     * Resolve the texture-Z voxel index currently under the view's
     * plane-intersection point for the active ROI, or `undefined`
     * when unavailable.
     */
    private currentSliceZForActiveRoi(): number | undefined {
        const texture = this.getRoi?.()?.texture;
        if (!texture) {
            return undefined;
        }
        return this.currentTextureVoxelZ(texture);
    }

    /**
     * Document-level keydown handler (capture phase). Consumes the
     * `Esc` key to discard a pending suggestion. When no suggestion
     * is active, the event passes through untouched so existing
     * Esc behaviours (e.g. brush stroke cancellation) keep working.
     *
     * Modifier-only Esc — any combination with Ctrl/Alt/Shift/Meta is
     * ignored to avoid colliding with browser or OS shortcuts.
     */
    private onDocumentKeyDown(e: KeyboardEvent): void {
        if (!this.pending) {
            return;
        }
        if (e.key !== 'Escape') {
            return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.discardPending();
    }

    /**
     * Document-level mousedown handler (capture phase). Two left-click
     * gestures on the active view's canvas, both while a suggestion
     * is pending:
     *
     *   - `Ctrl + left-click` → **accept**. The click is consumed
     *     (`preventDefault` + `stopPropagation`) so the active tool
     *     does not also start an action; the suggestion is committed
     *     and the view stays on the current slice.
     *
     *   - plain `left-click` (no modifiers) → **draw-to-tweak**, but
     *     only when {@link allowPlainClickTweak} is set (brush). The
     *     suggestion is materialised into the SDF synchronously, then
     *     the event is allowed to propagate so the brush stroke that
     *     follows edits the now-real contour (boolean-OR add). We must
     *     commit here, on mousedown, BEFORE the brush applies its
     *     stroke on mouseup — committing in `onContoursModified`
     *     (which fires after the stroke) would overwrite the edit.
     *     For tools where this is disabled (e.g. line-deform) a plain
     *     click is left untouched: the suggestion persists and is only
     *     ever committed via Ctrl+click.
     *
     * Capture phase guarantees this runs before
     * `ViewManager.handleEvent`'s bubble-phase canvas dispatch.
     */
    private onDocumentMouseDown(e: MouseEvent): void {
        if (!this.pending || !this.view) {
            return;
        }
        if (e.button !== 0) {
            return;
        }
        const canvas = this.view.canvas;
        if (!canvas || e.target !== canvas) {
            return;
        }

        const noSecondaryModifiers = !e.metaKey && !e.altKey && !e.shiftKey;

        if (e.ctrlKey && noSecondaryModifiers) {
            // Accept: consume the click so the active tool does not
            // also start an action on this slice.
            e.preventDefault();
            e.stopPropagation();
            this.accept();
            return;
        }

        if (!e.ctrlKey && noSecondaryModifiers && this.allowPlainClickTweak) {
            // Draw-to-tweak (brush only): materialise the suggestion,
            // then let the stroke proceed (do NOT consume the event).
            // Skip undo so the suggestion + brush stroke are recorded
            // as a single undoable operation.
            this.accept(/* skipUndo */ true);
        }
    }

    /**
     * Drop any rendered ghost immediately on contour modification.
     * The user is actively editing — they don't want a stale ghost
     * lingering — and a fresh evaluation is scheduled so the next
     * suggestion appears once they navigate to a new slice.
     */
    private onContoursModified(event: RoiContoursModifiedEvent): void {
        if (!this.enabled || !this.getRoi) {
            return;
        }
        const roi = this.getRoi();
        if (!roi || roi.roiNumber !== event.roiNumber) {
            return;
        }
        this.discardPending();
        this.scheduleEvaluation();
    }

    /**
     * Cancel any in-flight delay timer and queue a new evaluation.
     * Each slice change resets the timer so rapid scrolling never
     * triggers intermediate ghost renders — the suggestion only
     * appears once the user has stayed on the same slice for the
     * full {@link CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS} window.
     */
    private scheduleEvaluation(): void {
        if (!this.enabled) {
            return;
        }
        this.cancelPendingTimer();
        // Drop any previously rendered ghost while waiting for the
        // next evaluation — it is bound to a different slice.
        this.discardPending();
        this.pendingTimer = setTimeout(() => {
            this.pendingTimer = undefined;
            this.evaluateForCurrentSlice();
        }, CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS);
    }

    private cancelPendingTimer(): void {
        if (this.pendingTimer !== undefined) {
            clearTimeout(this.pendingTimer);
            this.pendingTimer = undefined;
        }
    }

    /**
     * Compute and render a suggestion for the currently visible
     * slice, if eligible. Source slice is auto-detected from the
     * adjacent slices: when exactly one neighbour has content, use
     * that; when both do, prefer the slice immediately above
     * (currentZ - 1) — this matches the typical top-to-bottom
     * contouring workflow.
     *
     * Slices are read directly from the GPU via
     * {@link SignedDistanceField.sliceToArray}, mirroring the
     * approach used by the interpolation tool. This bypasses the
     * texture's CPU pixel cache, which can be stale for newly
     * created ROIs whose SDF was just resized by `applyBoolean`
     * (the underlying SDF object is replaced inside
     * {@link ContourTexture.resetSdfAndGrid}, leaving the previous
     * cache reference unrelated to the new texture state).
     */
    private evaluateForCurrentSlice(): void {
        if (!this.enabled || !this.getRoi || !this.view) {
            return;
        }
        const roi = this.getRoi();
        if (!roi || !roi.canModify) {
            this.discardPending();
            return;
        }
        const texture = roi.texture;
        if (!texture || texture.isEmpty) {
            this.discardPending();
            return;
        }

        const sizeZ = texture.sizePx[2];
        if (sizeZ < 2) {
            this.discardPending();
            return;
        }

        const currentVoxelZ = this.currentTextureVoxelZ(texture);
        if (currentVoxelZ === undefined) {
            this.discardPending();
            return;
        }

        const sdf = texture.signedDistanceField;
        const readSlice = (z: number): Uint8Array | undefined => {
            if (z < 0 || z >= sizeZ) {
                return undefined;
            }
            try {
                return sdf.sliceToArray(z);
            } catch (err) {
                console.warn(
                    'ContourAutoSuggestionController: sliceToArray failed',
                    err
                );
                return undefined;
            }
        };

        const currentSlice = readSlice(currentVoxelZ);
        if (!currentSlice) {
            this.discardPending();
            return;
        }
        // Current slice must be empty for a propagation to make
        // sense; otherwise the user already has content here.
        if (
            !SignedDistanceField.isSliceEmpty(
                currentSlice,
                ContourTexture.SDF_THRESHOLD
            )
        ) {
            this.discardPending();
            return;
        }

        // Build the source slice using multi-slice B-spline
        // interpolation. We gather up to 4 non-empty neighbour
        // slices (z-2, z-1, z+1, z+2) and blend them with cubic
        // B-spline weights for smoother transitions compared to
        // the previous linear midpoint blend. When fewer slices
        // are available the function degrades gracefully.
        const candidateOffsets = [-2, -1, 1, 2];
        const neighbourSlices: SliceWithPosition[] = [];

        for (const offset of candidateOffsets) {
            const z = currentVoxelZ + offset;
            const slice = readSlice(z);
            if (
                slice &&
                !SignedDistanceField.isSliceEmpty(
                    slice,
                    ContourTexture.SDF_THRESHOLD
                )
            ) {
                neighbourSlices.push({ data: slice, z });
            }
        }

        let sourceSlice: Uint8Array | undefined;
        if (neighbourSlices.length > 0) {
            try {
                sourceSlice = blendSlicesBSpline(
                    neighbourSlices,
                    currentVoxelZ
                );
            } catch (err) {
                console.warn(
                    'ContourAutoSuggestionController: B-spline blend failed, falling back to nearest copy',
                    err
                );
                // Fall back to the nearest neighbour
                sourceSlice = new Uint8Array(neighbourSlices[0].data);
            }
        }

        if (!sourceSlice) {
            this.discardPending();
            return;
        }

        const grid: Grid = texture.textureGrid;

        // Image-driven refinement: nudge the iso-surface toward
        // anatomical edges visible in the underlying image (works for
        // CT / PET / MR — see deriveRefinementOptions). The B-spline
        // blend gives smooth shape continuity along Z; the intensity-guided
        // passes add local boundary alignment. Failures here are
        // non-fatal — we keep the unrefined B-spline result so the
        // user always sees a preview.
        try {
            const intensitySlice = this.extractIntensitySliceForTexture(
                texture,
                currentVoxelZ
            );
            const refineOptions = intensitySlice
                ? this.deriveRefinementOptions(intensitySlice)
                : undefined;
            if (intensitySlice && refineOptions) {
                // mm→byte scale taken from the structure texture's own
                // quantization margin, so the refinement reflects this
                // SDF's actual encoding rather than a fixed default.
                const sdfBytesPerMm = texture.sdfBytesPerMm();
                // Pass 1: edge-based — pull iso toward intensity
                // gradients (anatomical edges).
                sourceSlice = refineSliceWithIntensityEdge(
                    sourceSlice,
                    intensitySlice,
                    grid.sizeX,
                    grid.sizeY,
                    sdfBytesPerMm,
                    refineOptions.edge
                );
                // Pass 2: region-based — contract/expand where the
                // absolute intensity clearly indicates interior vs
                // exterior (e.g. air inside a lung vs tissue on CT;
                // high uptake vs background on PET).
                sourceSlice = refineSliceWithIntensityRegion(
                    sourceSlice,
                    intensitySlice,
                    grid.sizeX,
                    grid.sizeY,
                    sdfBytesPerMm,
                    refineOptions.region
                );
            }
        } catch (err) {
            console.warn(
                'ContourAutoSuggestionController: intensity refinement failed, using B-spline result as-is',
                err
            );
        }

        // Emits vertices in voxel-centered continuous coordinates; the
        // renderer feeds those directly into `Grid.toWorldSpace`, which
        // applies the mm scaling (see extractSlicePolygons).
        const polygons = extractSlicePolygons(
            sourceSlice,
            grid.sizeX,
            grid.sizeY
        );
        if (polygons.length === 0) {
            this.discardPending();
            return;
        }

        this.pending = {
            roiNumber: roi.roiNumber,
            targetVoxelZ: currentVoxelZ,
            sourceSliceBytes: sourceSlice,
        };
        const structureColor = roi.color;
        const previewColor = lightenColor(structureColor, 0.15);

        this.renderer?.setState({
            polygonsVoxel: polygons,
            targetVoxelZ: currentVoxelZ,
            textureGrid: grid,
            roiColor: previewColor,
        });
    }

    private discardPending(): void {
        if (!this.pending) {
            // Always clear the renderer in case a previous render
            // was issued without our pending bookkeeping (defensive).
            this.renderer?.clear();
            return;
        }
        this.pending = undefined;
        this.renderer?.clear();
    }

    /**
     * Derive the refinement option overrides for the active
     * image's modality, scaling the intensity-unit thresholds so the
     * refinement works across CT / PET / MR rather than only CT.
     *
     *   - **CT** (calibrated Hounsfield Units): use the proven fixed
     *     defaults — return empty overrides.
     *   - **Non-CT** (PET activity/SUV, MR arbitrary units): scale
     *     intensity-unit thresholds via
     *     {@link scaleRefinementOptionsForSpread}.
     *
     * Returns `undefined` when a non-CT slice's intensity spread
     * cannot be characterised, signalling the caller to skip
     * refinement (keep the pure B-spline result) rather than chase
     * noise.
     */
    private deriveRefinementOptions(intensitySlice: Float32Array):
        | {
              edge: Partial<EdgeRefinementOptions>;
              region: Partial<RegionRefinementOptions>;
          }
        | undefined {
        const modality =
            this.view?.environment.image?.parent?.firstSlice.modality;

        // CT is calibrated — the intensity defaults are physically tuned.
        if (modality === undefined || modality === Modality.CT) {
            return { edge: {}, region: {} };
        }

        const spread = estimateRobustIntensitySpread(intensitySlice);
        if (spread === undefined) {
            return undefined;
        }

        return scaleRefinementOptionsForSpread(spread);
    }

    /**
     * Resolve the active image's intensity buffer + grid and delegate
     * the grid-aligned sampling to the shared
     * {@link extractIntensitySliceAlignedToGrid} helper (which both
     * this tool and the Interpolation tool use). Returns `undefined`
     * when no image is bound, the image has no float pixel data, the
     * grids are not voxel-aligned, or the target Z is outside the
     * volume.
     *
     * The float-precision intensity buffer lives on the underlying
     * {@link Image3D} (the un-transformed source); using the parent's
     * grid avoids any registration transform on secondary images and
     * keeps indexing consistent with the buffer layout.
     */
    private extractIntensitySliceForTexture(
        texture: ContourTexture,
        targetVoxelZ: number
    ): Float32Array | undefined {
        const image = this.view?.environment.image;
        const parent = image?.parent;
        if (!parent) {
            return undefined;
        }
        const floatData = parent.floatPixelData;
        if (!floatData) {
            return undefined;
        }
        return extractIntensitySliceAlignedToGrid(
            texture.textureGrid,
            parent.grid,
            floatData,
            targetVoxelZ
        );
    }

    /**
     * Resolve the texture's Z voxel index that corresponds to the
     * view's current plane-intersection point. Returns undefined
     * when the intersection point falls outside the texture's Z
     * extent.
     *
     * The Grid convention places voxel CORNERS at integer voxel
     * coordinates and voxel CENTERS at the half-integers
     * (`Grid.fromDicom` subtracts a half-voxel from the DICOM patient
     * position to derive the corner-aligned transformation). Combined
     * with `PlaneView.snapToImagePixel`, the active slice's
     * intersection point lands exactly on a voxel center, i.e. at
     * `voxel.z = N + 0.5` for visual slice `N`. `Math.floor` returns
     * `N` (the voxel that contains the centre); `Math.round` would
     * return `N + 1` because JavaScript rounds half-up — which is
     * what caused the suggestion to be computed for the slice next
     * to the one the user was actually on. The codebase uses
     * `Math.floor` for this same conversion in `LineDeformTool`.
     */
    private currentTextureVoxelZ(texture: ContourTexture): number | undefined {
        if (!this.view) {
            return undefined;
        }
        const voxel = texture.textureGrid.toVoxelSpace(
            this.view.planeIntersectionPoint.coordinates
        );
        const z = Math.floor(voxel[2]);
        if (z < 0 || z >= texture.sizePx[2]) {
            return undefined;
        }
        return z;
    }
}
